<template>
  <section id="works" class="common-padding mb-20">
    <div class="flex flex-col">
      <h3
        id="selectedWorks"
        v-html="selectedWorks"
        class="heading-1 text-start leading-none font-bold uppercase"
      ></h3>
      <p
        class="heading-1 text-flax-smoke-400 text-opacity-50 hidden w-4/5 text-end font-extrabold sm:block"
      >
        ( {{ selectedWorksProps.length }} )
      </p>

      <div
        id="selected-works-text"
        class="md:column-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 text-center text-nowrap lg:col-start-2"
        >
          (
          <span class="inline sm:hidden">{{ selectedWorksProps.length }} </span>
          PROJECTS )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Passion projects centered on perception and autonomy for embodied AI, alongside technical solutions spanning AI/ML, EdTech, and accessibility tech.
        </p>
      </div>
    </div>

    <div
      class="sm:column-gap relative mt-12 grid size-full grid-cols-12 lg:mt-[10%]"
    >
      <!-- Counter that transitions between all numbers -->
      <div
        v-show="isCounterActive"
        id="counterRef"
        ref="counterRef"
        class="text-flax-smoke-100 hidden h-fit w-auto text-[22vw] leading-[0.8] font-semibold md:flex md:fixed md:left-[3%] md:top-12 md:z-[200] pointer-events-none overflow-y-hidden overflow-x-visible pr-[0.15em] opacity-0"
      >
        <span class="font-title! relative -tracking-wider">0</span>
        <span
          id="index"
          ref="indexRef"
          class="font-title! relative -tracking-wider will-change-transform"
          >{{ index + 1 }}.</span
        >
      </div>
      <aside
        @mouseenter="showCursor"
        @mouseleave="hideCursor"
        class="relative col-span-full flex flex-col space-y-16 md:col-start-6 md:col-span-7"
      >
        <!-- Project cards -->
        <div
          v-for="(work, i) in selectedWorksProps"
          :key="i"
          class="work-card @container"
        >
          <!-- Project content -->
          <div class="group">
            <!-- Media section -->
            <a
              :href="work.videoLinkOverride || work.url"
              target="_blank"
              rel="noopener noreferrer"
              class="block"
              v-if="work.videoSrc || work.imageBg"
            >
              <div class="flex-center relative aspect-square overflow-clip rounded-lg">
                <img
                  alt="work-background"
                  loading="lazy"
                  class="absolute size-full object-cover select-none"
                  :src="work.imageBg"
                />
                <div v-if="work.videoSrc" class="flex-center z-10 aspect-4/3 size-full overflow-clip rounded-lg object-cover">
                  <video
                    ref="videoRefs"
                    :src="work.videoSrc"
                    muted
                    autoplay
                    loop
                    playsinline
                    data-autoplay="true"
                    class="size-[80%] rounded-md object-contain blur transition-all duration-500 ease-in-out"
                  ></video>
                </div>
              </div>
            </a>
            <!-- Content section -->
            <div>
              <p class="heading-6 font-title! mt-8 sm:mt-[2%] mb-[1%] leading-none">
                {{ work.category }}
              </p>
              <div class="items-center justify-between sm:flex">
                <h3 class="heading-3 font-title! font-bold uppercase mt-4 sm:mt-0">
                  {{ work.name }}
                </h3>
              </div>
              <div class="flex flex-wrap gap-1.5 select-none mt-3">
                <p
                  class="border-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-4 py-2 transition-[background-color,color] duration-500 ease-in-out"
                  v-for="tag in work.tags"
                  :key="tag"
                >
                  <span>{{ tag }}</span>
                </p>
              </div>
              <p v-if="work.description" class="text-flax-smoke-300 text-sm mt-3 leading-relaxed">
                {{ work.description }}
              </p>
              <div class="flex flex-wrap gap-3 mt-4">
                <a
                  :href="work.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="border-flax-smoke-300 bg-flax-smoke-300 text-flax-smoke-900 hover:text-flax-smoke-300 rounded-full border px-4 py-2 text-sm transition-[background-color,color] duration-500 ease-in-out hover:bg-transparent inline-block"
                >
                  <span>{{ work.githubUrl ? 'Live Site' : 'GitHub' }}</span>
                </a>
                <a
                  v-if="work.huggingfaceUrl"
                  :href="work.huggingfaceUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="border-flax-smoke-300 text-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-4 py-2 text-sm transition-[background-color,color] duration-500 ease-in-out inline-block"
                >
                  <span>Hugging Face</span>
                </a>
                <a
                  v-if="work.githubUrl"
                  :href="work.githubUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="border-flax-smoke-300 text-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-4 py-2 text-sm transition-[background-color,color] duration-500 ease-in-out inline-block"
                >
                  <span>GitHub</span>
                </a>
                <a
                  v-if="work.demoUrl"
                  :href="work.demoUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="border-flax-smoke-300 text-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-4 py-2 text-sm transition-[background-color,color] duration-500 ease-in-out inline-block"
                >
                  <span>Demo</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
  import gsap from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  gsap.registerPlugin(ScrollTrigger);
  import { useWindowSize } from '@vueuse/core';
  import { projectComparavision, projectVoiceforgood, projectLingolink, projectYu, bioproj, projectVoxae, projectOrcaCtl } from '@/assets/videos';
  import { workBg1, workBg2, workBg3, workBg4, workBg5 } from '@/assets/images';

  const isSmallScreen = computed(() => {
    return useWindowSize().width.value < 768;
  });

  const index = ref(0);
  const selectedWorks = ref('Projects /');
  const isCounterActive = ref(false);
  const videoRefs = ref<HTMLVideoElement[]>([]);

  const tl = gsap
    .timeline({ defaults: { duration: 0.25 } })
    .to(['#cursor', '#inner'], {
      scale: 1,
      opacity: 1,
    })
    .paused(true);

  const showCursor = () => {
    tl.play();
  };
  const hideCursor = () => {
    tl.reverse();
  };

  const selectedWorksProps = [
    {
      name: 'Voxae',
      category: 'Language-Grounded Scene Understanding for Physical AI',
      description: 'Ask an aerial scene a question in plain language and get back the exact pixels that answer it. A vision language model\'s hidden state is projected directly into SAM 2.1\'s prompt space and trained end to end with LoRA. Beats a zero shot VLM + SAM baseline by 45% on affordance queries at 83x lower latency (0.25s vs 20.8s per query).',
      tags: ['Python', 'PyTorch', 'Transformers', 'Qwen2-VL', 'SAM 2.1', 'PEFT/LoRA', 'Gradio', 'Hugging Face'],
      videoSrc: projectVoxae,
      imageBg: workBg5,
      url: 'https://voxae.vercel.app/',
      huggingfaceUrl: 'https://huggingface.co/spaces/nhibuilds/voxae',
      githubUrl: 'https://github.com/nhipixel/voxae',
    },
    {
      name: 'ComparaVision (HackThe6ix)',
      category: 'AI Model Comparison Platform',
      description: 'Lets teams benchmark vision language models side by side instead of testing each one manually. Automates evaluation across 10+ performance and eco sustainability metrics using PyTorch for inference and a Next.js dashboard for results. Cuts manual comparison time by 72%.',
      tags: ['Python', 'PyTorch', 'Next.js', 'React', 'Tailwind CSS', 'Node.js', 'Express.js'],
      videoSrc: projectComparavision,
      imageBg: workBg1,
      url: 'https://github.com/nhipixel/comparavision-integration/tree/main',
    },
    {
      name: 'VoiceForGood',
      category: 'AI SaaS Platform',
      description: 'Turns long form nonprofit interviews into short form social clips (TikToks, Shorts, Reels) without a video editor. Built as a full stack AI SaaS platform using Next.js, FastAPI, and the Gemini API for clip generation, with Stripe billing and AWS deployment. Processed 52 interviews for 3+ community organizations.',
      tags: ['Python', 'Gemini API', 'Prisma', 'PostgreSQL','FastAPI', 'Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'AWS'],
      videoSrc: projectVoiceforgood,
      imageBg: workBg2,
      url: 'https://github.com/nhipixel/voiceforgood-ai-testimonial-highlighter',
    },
    {
      name: 'orca-ctl (Hack the North)',
      category: 'Remote Autonomous Server Recovery via Hardware I/O',
      description: 'Recovers bricked servers during a data center outage by reading the display over HDMI and emulating a USB keyboard and mouse, with no software installed on them. A Raspberry Pi 5 on QNX captures HDMI frames in C and forwards input over UART to an Arduino Leonardo; a Go and Next.js dashboard runs an OpenAI planner plus a vision agent that checks each step. Demoed running remotely on all of our team\'s laptops.',
      tags: ['C', 'C++', 'Go', 'Gin', 'TypeScript', 'Next.js', 'QNX', 'Raspberry Pi 5', 'Arduino', 'OpenAI API'],
      videoSrc: projectOrcaCtl,
      imageBg: workBg4,
      url: 'https://github.com/anthskti/hackthenorth',
      demoUrl: 'https://www.youtube.com/watch?v=W0umiOq4xsg',
    },
    {
      name: 'Epigenetic Markers of Memory Loss',
      category: 'Early Alzheimer\'s Detection via DNA Methylation Data Platform',
      description: 'Predicts Alzheimer\'s risk from a routine blood test instead of invasive procedures, using DNA methylation data. Processes ~850k CpG sites through a PyTorch CNN and XGBoost ensemble with 5 fold cross validation, and surfaces SHAP visualizations to explain which biomarkers drove each prediction. Trained on AWS with an interactive dashboard for researchers.',
      tags: ['AWS', 'Python', 'PyTorch', 'XGBoost', 'FastAPI', 'Next.js', 'React', 'TypeScript'],
      videoSrc: bioproj,
      imageBg: workBg5,
      videoLinkOverride: 'https://www.youtube.com/watch?v=-s9DWOG8gIM',
      url: 'https://github.com/nhipixel/alzheimers-dna-early-detection-platform',
    },
    {
      name: 'LingoLink',
      category: 'EdTech Language Exchange Platform',
      description: 'Connects language learners for real time conversation practice instead of static lesson content. Built on the MERN stack with a friend system, direct messaging, and Stream API video calls with screen sharing. Tested with 10+ beta users.',
      tags: ['JavaScript', 'Node.js', 'React', 'Tailwind CSS', 'Express.js', 'MongoDB', 'Stream', 'JWT'],
      videoSrc: projectLingolink,
      imageBg: workBg3,
      url: 'https://github.com/nhipixel/lingolink-language-learning-social-network',
    },
    {
      name: 'YorkU Smart Parking',
      category: 'Smart Parking System',
      description: 'Tracks parking slot availability in real time instead of relying on manual checks. Built as a Java Swing desktop application with JaCoCo for test coverage and iterative development cycles. Led a 5 person team through design and delivery.',
      tags: ['Java', 'Java Swing', /* 'JUnit', */ 'JaCoCo', /* 'Agile Methodologies', */ 'Git'],
      videoSrc: projectYu,
      imageBg: workBg4,
      url: 'https://github.com/nhipixel/smart-parking-yorku',
    },
  ];

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement;
      if (entry.isIntersecting) {
        video.play();
        video.classList.remove('blur');
      } else {
        if (!video.paused) video.pause();
      }
    });
  };

  const stopAllVideos = () => {
    videoRefs.value?.forEach((video: HTMLVideoElement) => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });
  };

  onBeforeMount(() => {
    selectedWorks.value = textSplitterIntoChar('Projects / ', true);
  });

  onMounted(() => {
    stopAllVideos();

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.75,
    });

    videoRefs.value = Array.from(document.querySelectorAll('#works video'));
    videoRefs.value.forEach((video) => {
      observer.observe(video);
    });

    animateSplitText(
      '#selectedWorks .letters',
      '#selected-works-text',
      0.7,
      0.01,
      0,
    );

    if (!isSmallScreen.value) {
      const cards = gsap.utils.toArray<HTMLElement>('.work-card');
      
      // Set up ScrollTrigger for first project to show counter
      if (cards.length > 0) {
        const firstCard = cards[0] as HTMLElement;
        const firstMedia = firstCard.querySelector('.aspect-square') || firstCard;
        if (firstMedia) {
          ScrollTrigger.create({
            trigger: firstMedia,
            start: 'top 15%',
            end: 'bottom 15%',
            onEnter: () => {
              index.value = 0;
              isCounterActive.value = true;
              gsap.to('#counterRef', { 
                opacity: 1,
                duration: 0.7,
                ease: 'power2.inOut'
              });
            },
            onEnterBack: () => {
              index.value = 0;
              isCounterActive.value = true;
              gsap.to('#counterRef', { 
                opacity: 1,
                duration: 0.7,
                ease: 'power2.inOut'
              });
            },
            onLeaveBack: () => {
              gsap.to('#counterRef', { 
                opacity: 0,
                duration: 0.5,
                ease: 'power2.inOut',
                onComplete: () => {
                  isCounterActive.value = false;
                }
              });
            },
          });
        }
      }

      // Set up animations for transitions between projects
      cards.forEach((div: HTMLElement, i: number) => {
        if (i > 0) { // Skip first card's animation
          ScrollTrigger.create({
            trigger: div,
            start: 'top 25%',
            end: 'bottom 25%',
            onEnter: () => {
              // Forward animation
              gsap.to('#index', {
                yPercent: -100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  index.value = i;
                  gsap.set('#index', { yPercent: 100 });
                  gsap.to('#index', {
                    yPercent: 0,
                    duration: 0.3,
                    ease: 'power4.inOut',
                  });
                },
              });
            },
            onLeaveBack: () => {
              // Backward animation
              gsap.to('#index', {
                yPercent: 100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  index.value = i - 1;
                  gsap.set('#index', { yPercent: -100 });
                  gsap.to('#index', {
                    yPercent: 0,
                    duration: 0.3,
                    ease: 'power4.inOut',
                  });
                },
              });
            },
          });
        }
      });

      // Hide counter after last project
      if (cards.length > 0) {
        const lastCard = cards[cards.length - 1];
        ScrollTrigger.create({
          trigger: lastCard,
          start: 'bottom 40%',
          onEnter: () => {
            gsap.to('#counterRef', {
              opacity: 0,
              duration: 0.5,
              ease: 'power2.inOut',
              onComplete: () => {
                isCounterActive.value = false;
              }
            });
          },
          onEnterBack: () => {
            isCounterActive.value = true;
            gsap.to('#counterRef', {
              opacity: 1,
              duration: 0.7,
              ease: 'power2.inOut'
            });
          },
        });
      }
    }
  });

  onUnmounted(() => {
    ScrollTrigger.getAll().forEach((t) => t.kill());
  });
</script>